// src/components/Groups/ParticipantsListView.tsx
import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Col,
  Input,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label
} from 'reactstrap';
import { CheckCircle, ChevronLeft, Search, Users, Circle } from 'react-feather';
import { Image, P } from '../../../../AbstractElements';
import { dynamicImage } from '../../../../Utils';
import { toast } from 'react-toastify';
import { useInviteParticipant } from '../../../../hooks/useInviteParticipant';
import { useFetchGroups } from '../../../../hooks/useFetchGroups';
import { group } from 'console';
import Authorized from '../../../../CommonElements/Authorisation/Authorise';

interface ParticipantsListViewProps {
  participants?: any[]; // optional, fallback to location.state
  groupTitle?: string;
}

export default function ParticipantsListView(props: ParticipantsListViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state || {}) as any;

  // const initialParticipantsProp: any[] = props.participants ?? fromState.participants ?? [];
  const groupTitle = props.groupTitle ?? fromState.groupTitle ?? 'Participants';
  const groupId = fromState.groupId ?? (props as any).groupId ?? '';

  const userId = JSON.parse(String(localStorage.getItem('user')))?.id || '';
  const role = JSON.parse(String(localStorage.getItem('user')))?.role || '';
  const { error, refetch, userSpecificGroups,fetchUserSpecificGroups } = useFetchGroups(userId, role); // Add refetch function

  const groupParticipants = useMemo(
    () => userSpecificGroups?.find((g: any) => g._id === groupId)?.participantsMerged || [],
    [userSpecificGroups, groupId]
  );

  const [participants, setParticipants] = useState<any[]>(groupParticipants || []);

  useEffect(() => {
    setParticipants(groupParticipants);
  }, [groupParticipants]);

  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);

  // detect theme: body.dark or data-theme="dark"
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' && (document.body.classList.contains('dark') || document.documentElement.getAttribute('data-theme') === 'dark')
  );

  // observe theme changes (if your app toggles class dynamically)
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setIsDark(document.body.classList.contains('dark') || document.documentElement.getAttribute('data-theme') === 'dark');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => obs.disconnect();
  }, []);

  // filter
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return participants;
    return participants.filter(p => {
      const name = `${p.firstname ?? ''} ${p.lastname ?? ''}`.toLowerCase();
      const email = (p.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [search, participants]);

  // pagination
  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [pages]); // eslint-disable-line

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // CSV export
  const handleExport = () => {
    if (!filtered || filtered.length === 0) return;
    const rows = filtered.map(p => ({
      id: p._id,
      name: `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim(),
      email: p.email ?? ''
    }));
    const header = Object.keys(rows[0] || {}).join(',');
    const body = rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const csv = [header, body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${groupTitle.replace(/\s+/g, '_')}_participants.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Invite hook - pass groupId and a callback to add newly invited participant to UI (optimistic)
  const {
    isOpen,
    firstname,
    lastname,
    email,
    loading,
    errors,
    setFirstname,
    setLastname,
    setEmail,
    toggle,
    submit
  } = useInviteParticipant({
    groupId: String(groupId),
    onSuccess: async (respData) => {
      // If backend returns the created user object (or invite), try to add user to list for immediate feedback.
      // We attempt to read respData.invite or respData.user; adapt depending on backend response shape.
      const maybeInvite = respData?.invite ?? respData;
      // If user created or invite contains userId/email/name, append a minimal entry
      if (maybeInvite) {
        const newParticipant = (maybeInvite.userId || maybeInvite.user) ? {
          _id: maybeInvite.userId?._id ?? maybeInvite.userId ?? maybeInvite.user?._id ?? `${Date.now()}`,
          firstname: maybeInvite.firstname ?? firstname ?? '',
          lastname: maybeInvite.lastname ?? lastname ?? '',
          email: maybeInvite.email ?? email ?? ''
        } : {
          _id: `invited-${Date.now()}`,
          firstname: firstname ?? '',
          lastname: lastname ?? '',
          email: email ?? ''
        };
        // setParticipants(prev => [newParticipant, ...prev]);
        await fetchUserSpecificGroups();
      } else {
        // fallback: add using form fields
        setParticipants(prev => [{
          _id: `invited-${Date.now()}`,
          firstname: firstname ?? '',
          lastname: lastname ?? '',
          email: email ?? ''
        }, ...prev]);
      }
    }
  });

  // Unique root class so styles are scoped and we can include the <style> tag safely
  const rootClass = 'participants-page-root';

  return (
    <Col xxl={12} xl={12} lg={12} md={12} className={`${rootClass}-container`}>
      {/* Embedded component-only styles */}
      <style>{`
        .${rootClass}-container {
          min-height: calc(100vh - var(--app-header-height, 64px));
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1rem;
          box-sizing: border-box;
          background: transparent;
        }
        .${rootClass}-card {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          overflow: hidden;
          border-radius: 8px;
        }
        .${rootClass}-card .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
        }
        .${rootClass}-table-wrap {
          flex: 1 1 auto;
          overflow: auto;
          padding: 0.5rem 1rem;
        }
        .${rootClass}-table-wrap .table {
          margin-bottom: 0;
          min-width: 600px;
        }
        .${rootClass}-card .card-footer {
          flex: 0 0 auto;
          border-top: 1px solid rgba(0,0,0,0.06);
          padding: 0.75rem 1rem;
          background: transparent;
        }
        @media (max-width: 575.98px) {
          .${rootClass}-card .card-header { padding: 0.6rem; }
          .${rootClass}-table-wrap { padding: 0.5rem; }
        }
        body.dark .${rootClass}-card {
          background: #0b1220;
          color: #e6eef8;
        }
        body.dark .${rootClass}-card .card-footer {
          border-top-color: rgba(255,255,255,0.04);
        }
        .${rootClass}-empty {
          padding: 48px 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--bs-muted, #6c757d);
        }

        /* Invite button */
        .invite-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid rgba(37,99,235,0.12);
          background: transparent;
          color: ${isDark ? '#fff' : '#2563eb'};
        }
        .invite-btn:hover { background: ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(37,99,235,0.05)'}; }

       
      `}</style>

      <Card className={`${rootClass}-card ${isDark ? 'bg-dark text-white' : ''}`}>
        <CardHeader className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <Button color="link" onClick={() => navigate(-1)} title="Back" className="p-0">
              <ChevronLeft color={isDark ? 'white' : 'black'} />
            </Button>

            <div>
              <h5 className="mb-0">{groupTitle}</h5>
              <small className="text-muted">Participants ({total})</small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center border rounded px-2">
              <Search size={14} />
              <Input
                bsSize="sm"
                className="border-0 ms-2"
                placeholder="Search name or email"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ minWidth: 180, background: 'transparent' }}
              />
            </div>

            <div className="d-flex align-items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="form-select form-select-sm"
                style={{ minWidth: 88 }}
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>

              <Authorized allowed={['admin', 'group leader']}>
                <button
                  className="invite-btn"
                  onClick={toggle}
                  type="button"
                  aria-label="Invite participant"
                  title="Invite new participant"
                >
                  <Users size={16} />
                  <span className="d-none d-sm-inline">Invite</span>
                </button>
              </Authorized>
            </div>
          </div>
        </CardHeader>

        <CardBody className="p-0">
          <div className={`${rootClass}-table-wrap`}>
            <div className="table-responsive">
              <Table className={`${isDark ? 'table table-dark table-hover mb-0' : 'table table-hover mb-0'}`}>
                <thead>
                  <tr>
                    <th style={{ width: 56 }}></th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Invitation Accepted</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className={`${rootClass}-empty`}>
                          <div>
                            <P className="mb-0">No participants found.</P>
                            <small className="text-muted">Try changing your search or page size.</small>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginated.map((p) => (
                      <tr key={p._id}>
                        <td style={{ width: 56 }}>
                          {/* <Image
                            className="img-30 rounded-circle"
                            src={dynamicImage(`user/${p.image || '1.jpg'}`)}
                            alt={p.firstname}
                          /> */}
                        </td>
                        <td>
                          <strong>{p.firstname} {p.lastname}</strong>
                        </td>
                        <td>{p.email}</td>
                        <td>{p.participantJoined ? <CheckCircle /> : <Circle />}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </CardBody>

        <CardFooter className="d-flex align-items-center justify-content-between">
          <div>
            <small className="text-muted">
              Showing {total === 0 ? 0 : ((page - 1) * pageSize + 1)} - {Math.min(total, page * pageSize)} of {total}
            </small>
          </div>

          <div>
            <nav aria-label="participants-pagination">
              <ul className="pagination mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))} type="button">Prev</button>
                </li>
                <li className="page-item disabled"><button className="page-link" type="button">{page}</button></li>
                <li className={`page-item ${page >= pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => Math.min(pages, p + 1))} type="button">Next</button>
                </li>
              </ul>
            </nav>
          </div>
        </CardFooter>
      </Card>

      {/* Invite Modal */}
      <Modal isOpen={isOpen} toggle={toggle} contentClassName="invite-modal" centered>
        <ModalHeader toggle={toggle} charCode="×" style={{ borderBottom: 'none' }}>
          <div style={{ minWidth: 320 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Invite participant</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>Enter participant details and send invite</div>
          </div>
        </ModalHeader>

        <ModalBody>
          <Form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <FormGroup>
              <Label className="form-label">First name</Label>
              <Input
                bsSize="sm"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                placeholder="First name"
                autoFocus
              />
              {errors?.firstname && <div style={{ color: 'var(--bs-danger,#dc3545)', fontSize: 12, marginTop: 6 }}>{errors.firstname}</div>}
            </FormGroup>

            <FormGroup>
              <Label className="form-label">Last name</Label>
              <Input
                bsSize="sm"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                placeholder="Last name"
              />
              {errors?.lastname && <div style={{ color: 'var(--bs-danger,#dc3545)', fontSize: 12, marginTop: 6 }}>{errors.lastname}</div>}
            </FormGroup>

            <FormGroup>
              <Label className="form-label">Email</Label>
              <Input
                bsSize="sm"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
              {errors?.email && <div style={{ color: 'var(--bs-danger,#dc3545)', fontSize: 12, marginTop: 6 }}>{errors.email}</div>}
              <div className="hint">An invite email will be sent to this address with a 6-digit group code.</div>
            </FormGroup>
          </Form>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
            <Button color="secondary" outline onClick={toggle} disabled={loading}>Cancel</Button>
            <Button color="primary" onClick={() => submit()} disabled={loading}>
              {loading ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </Col>
  );
}
